import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { decideWatch, watchDestination } from './gate';

import { useAuth } from '@/lib/auth/useAuth';
import { browseQueries, selectHero } from '@/lib/queries/browse';
import { freemiumQueries } from '@/lib/queries/freemium';

/**
 * Hero stage. A random currently-live stream is featured (re-rolled per load),
 * shown as the host's identity over the dark stage; with no live data it falls
 * back to the admin-featured VOD's cover poster, and with neither it shows the
 * empty placeholder. No playback chrome here — a real live/VOD preview is a
 * later increment. Anonymous viewers get a sign-in prompt instead of playback.
 */
export function HeroPlayer() {
  const { session } = useAuth();
  const signedIn = Boolean(session);
  const { data: live } = useQuery(browseQueries.liveNow());
  const { data: featured } = useQuery(browseQueries.featuredVod());
  const { data: status } = useQuery(freemiumQueries.status(signedIn));

  // One roll per load / data change — selectHero defaults to Math.random.
  const selection = useMemo(() => selectHero(live ?? [], featured ?? null), [live, featured]);

  const isLive = selection.kind === 'live';
  const title =
    selection.kind === 'live'
      ? selection.stream.title
      : selection.kind === 'featured'
        ? selection.vod.title
        : null;
  const creatorName =
    selection.kind === 'live'
      ? selection.stream.creatorName
      : selection.kind === 'featured'
        ? selection.vod.creatorName
        : null;
  const streamHref =
    selection.kind === 'live'
      ? selection.stream.watchHref
      : selection.kind === 'featured'
        ? selection.vod.watchHref
        : null;
  const posterUrl = selection.kind === 'featured' ? selection.vod.thumbnailUrl : null;
  const liveAvatarUrl = selection.kind === 'live' ? selection.stream.creatorAvatarUrl : null;

  const decision =
    signedIn && !status
      ? 'play' // fail-open while status loads/errors
      : decideWatch({
          kind: isLive ? 'live' : 'vod',
          signedIn,
          isMember: status?.isMember ?? false,
          unlocked:
            selection.kind === 'featured' && status
              ? status.unlockedIds.includes(selection.vod.id)
              : false,
          remaining: status?.remaining ?? 0,
        });
  const cta = streamHref
    ? {
        href: watchDestination(decision, streamHref),
        label:
          decision === 'play'
            ? isLive
              ? 'Watch live'
              : 'Watch'
            : decision === 'upgrade'
              ? 'Members watch'
              : 'Sign in to watch',
      }
    : !signedIn
      ? { href: '/auth/sign-in', label: 'Sign in to watch' }
      : null;

  return (
    <section className="relative aspect-[21/9] overflow-hidden rounded-lg bg-video-inset ring-1 ring-video-line">
      {/* Stage background: featured VOD → its cover poster; live → the host's
          identity over the dark inset; neither → the bare inset placeholder. */}
      {posterUrl ? (
        <img src={posterUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : isLive ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <HeroLiveAvatar name={creatorName ?? ''} avatarUrl={liveAvatarUrl} />
        </div>
      ) : null}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-video-bg via-video-bg/30 to-transparent" />

      {isLive && (
        <span className="absolute left-4 top-4 rounded-sm bg-oxblood-live px-2 py-0.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text">
          Live
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 p-6">
        {title ? (
          <h1 className="font-serif text-2xl text-video-text sm:text-3xl">{title}</h1>
        ) : (
          <p className="font-sans text-sm text-video-text-faint">
            No live streams right now — a featured stream will appear here.
          </p>
        )}
        {creatorName && (
          <p className="mt-1 font-sans text-sm text-video-text-muted">{creatorName}</p>
        )}
        {cta && (
          <a
            href={cta.href}
            className="mt-4 inline-block rounded-md bg-video-text px-5 py-2 font-sans text-sm font-medium text-video-bg transition hover:opacity-90"
          >
            {cta.label}
          </a>
        )}
      </div>
    </section>
  );
}

/** Live hero identity: the host avatar in the liveness ring, initials fallback. */
function HeroLiveAvatar({ name, avatarUrl }: { name: string; avatarUrl: string | null }) {
  const ring = 'ring-2 ring-oxblood-live';
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={`h-24 w-24 rounded-full bg-video-card object-cover ${ring}`}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`flex h-24 w-24 items-center justify-center rounded-full bg-video-card font-serif text-3xl text-video-text-muted ${ring}`}
    >
      {name.trim().charAt(0).toUpperCase() || '•'}
    </span>
  );
}
